import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { assetUrl, SafeImage } from "@/lib/assetUrl";
import { formatDateTime } from "@/lib/datetime";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Sparkles, Wand2, Check, X, Loader2, Video, Image as ImageIcon, User, Clock, Filter, Trash2,
  AlertTriangle, AlertCircle, Zap, Instagram, Facebook, Music2, MessageCircle, Mail, Download, Mic,
} from "lucide-react";
import { toast } from "sonner";
import { CreditsBar } from "@/pages/ConsumoIA";
import { Avatar } from "@/pages/Perfil";

const PLATFORM_TARGETS = [
  { key: "instagram_post",   label: "Instagram Post",   dim: "1:1",  aspect: "1:1",  icon: Instagram },
  { key: "instagram_story",  label: "Instagram Story",  dim: "9:16", aspect: "9:16", icon: Instagram },
  { key: "facebook_post",    label: "Facebook Post",    dim: "1:1",  aspect: "1:1",  icon: Facebook },
  { key: "facebook_cover",   label: "Facebook Portada", dim: "16:9", aspect: "16:9", icon: Facebook },
  { key: "tiktok",           label: "TikTok",           dim: "9:16", aspect: "9:16", icon: Music2 },
  { key: "whatsapp",         label: "WhatsApp",         dim: "1:1",  aspect: "1:1",  icon: MessageCircle },
  { key: "email_banner",     label: "Email Banner",     dim: "16:9", aspect: "16:9", icon: Mail },
];

const STATUS_LABELS = {
  pendiente_aprobacion: "Pendiente",
  aprobada: "Aprobada",
  rechazada: "Rechazada",
};

// Meta por plataforma para renderizar badges/iconos en la galería (Fase 8 fix).
const PLATFORM_META = {
  instagram_post:   { label: "IG Post",    icon: Instagram,     color: "#E1306C" },
  instagram_story:  { label: "IG Story",   icon: Instagram,     color: "#E1306C" },
  facebook_post:    { label: "FB Post",    icon: Facebook,      color: "#1877F2" },
  facebook_cover:   { label: "FB Portada", icon: Facebook,      color: "#1877F2" },
  tiktok:           { label: "TikTok",     icon: Music2,        color: "#000000" },
  whatsapp:         { label: "WhatsApp",   icon: MessageCircle, color: "#25D366" },
  email_banner:     { label: "Email",      icon: Mail,          color: "#00A36C" },
};

function PlatformBadges({ platforms, testid }) {
  if (!platforms || platforms.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {platforms.map((p) => {
        const meta = PLATFORM_META[p];
        if (!meta) return null;
        const Icon = meta.icon;
        return (
          <span
            key={p}
            className="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold"
            style={{
              backgroundColor: `${meta.color}15`,
              color: meta.color,
              borderColor: `${meta.color}40`,
            }}
            data-testid={`piece-platform-${p}`}
            title={meta.label}
          >
            <Icon className="h-2.5 w-2.5" />
            {meta.label}
          </span>
        );
      })}
    </div>
  );
}

const STATUS_STYLES = {
  pendiente_aprobacion: "bg-brand-gold-soft text-brand-gold-dark border-brand-gold/40",
  aprobada: "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/30",
  rechazada: "bg-destructive/10 text-destructive border-destructive/30",
};

const ASPECT_OPTIONS = [
  { value: "1:1", label: "Cuadrada 1:1", ratio: "aspect-square" },
  { value: "9:16", label: "Vertical 9:16", ratio: "aspect-[9/16]" },
  { value: "16:9", label: "Horizontal 16:9", ratio: "aspect-video" },
];

function fmt(iso) {
  if (!iso) return "—";
  return formatDateTime(iso);
}

// Fase 27.1 + 28 · assetUrl y SafeImage se centralizan en `@/lib/assetUrl`.

export default function MotorCreativo() {
  const { user, isMaster } = useAuth();
  const [tab, setTab] = useState("generar");

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="motor-creativo-page">
      <div>
        <div className="divider-gold mb-3" />
        <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
          <Wand2 className="h-8 w-8 text-brand-emerald" />
          Motor Creativo
          <Badge className="bg-brand-emerald-soft text-brand-emerald-dark border border-brand-emerald/30 font-semibold">
            IA REAL
          </Badge>
        </h1>
        <p className="text-sm text-stone-500 mt-2 max-w-2xl">
          Genere piezas visuales reales con IA (Gemini Nano Banana) desde un prompt.
          Las piezas quedan en flujo de aprobación antes de poder asignarse a
          publicaciones o campañas de correo.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="overflow-x-auto -mx-2 sm:mx-0">
          <TabsList data-testid="motor-tabs" className="min-w-max sm:min-w-0">
            <TabsTrigger value="generar" data-testid="tab-generate">Generar</TabsTrigger>
            <TabsTrigger value="subir" data-testid="tab-upload">Subir foto</TabsTrigger>
            <TabsTrigger value="galeria" data-testid="tab-gallery">Galería</TabsTrigger>
            {isMaster && (
              <TabsTrigger value="pendientes" data-testid="tab-pending">
                <span className="sm:hidden">Aprobación</span>
                <span className="hidden sm:inline">Pendientes de aprobación</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="video" data-testid="tab-video">Video</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="generar" className="mt-6"><GenerateTab /></TabsContent>
        <TabsContent value="subir" className="mt-6"><UploadPhotoTab /></TabsContent>
        <TabsContent value="galeria" className="mt-6"><GalleryTab isMaster={isMaster} currentUser={user} activeTab={tab} /></TabsContent>
        {isMaster && (
          <TabsContent value="pendientes" className="mt-6"><PendingTab isMaster={isMaster} /></TabsContent>
        )}
        <TabsContent value="video" className="mt-6"><VideoTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fase 29 · Subida de fotos a la galería creativa (jpg/png/webp ≤ 10MB)
// ---------------------------------------------------------------------------
function UploadPhotoTab() {
  const [files, setFiles] = useState([]); // File[]
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState([]); // pieces
  const fileInputRef = React.useRef(null);

  const onSelect = (fs) => {
    if (!fs) return;
    const arr = Array.from(fs).filter((f) => /image\/(png|jpe?g|webp)/i.test(f.type));
    setFiles((prev) => [...prev, ...arr].slice(0, 20));
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const upload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    const ok = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/creative/upload-photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok.push(r.data);
      } catch (e) {
        toast.error(`Falló "${f.name}": ${formatApiErrorDetail(e.response?.data?.detail) || "error"}`);
      }
    }
    setUploading(false);
    if (ok.length) {
      toast.success(`${ok.length} foto(s) subida(s) a la galería`);
      setUploaded((prev) => [...ok, ...prev].slice(0, 30));
      setFiles([]);
    }
  };

  return (
    <div className="space-y-6" data-testid="upload-photo-tab">
      <div
        className="card-elevated p-6 text-center border-2 border-dashed border-brand-emerald/30 hover:border-brand-emerald cursor-pointer transition"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onSelect(e.dataTransfer.files); }}
        data-testid="upload-photo-dropzone"
      >
        <ImageIcon className="h-10 w-10 text-brand-emerald mx-auto mb-2" />
        <div className="font-serif-display text-2xl text-brand-ink mb-1">Sube fotos a tu galería</div>
        <p className="text-sm text-stone-500 max-w-md mx-auto">
          Arrastra imágenes aquí, o haz clic para seleccionarlas. Formatos: JPG, PNG, WEBP.
          Se comprimen automáticamente si superan 3&nbsp;MB.
        </p>
        <input
          type="file" multiple accept="image/png,image/jpeg,image/webp"
          ref={fileInputRef}
          className="hidden"
          onChange={(e) => onSelect(e.target.files)}
          data-testid="upload-photo-input"
        />
      </div>

      {files.length > 0 && (
        <div className="card-elevated p-4">
          <div className="text-xs uppercase tracking-widest font-semibold text-stone-600 mb-2">
            Cola ({files.length})
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
            {files.map((f, i) => (
              <div key={i} className="relative aspect-square rounded overflow-hidden border border-border">
                <img
                  src={URL.createObjectURL(f)} alt=""
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => removeFile(i)}
                  data-testid={`upload-remove-${i}`}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center"
                >×</button>
              </div>
            ))}
          </div>
          <Button
            className="btn-primary mt-4"
            onClick={upload}
            disabled={uploading}
            data-testid="upload-photo-submit"
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImageIcon className="h-4 w-4 mr-2" />}
            Subir {files.length} foto{files.length !== 1 ? "s" : ""}
          </Button>
        </div>
      )}

      {uploaded.length > 0 && (
        <div className="card-elevated p-4">
          <div className="text-xs uppercase tracking-widest font-semibold text-brand-emerald-dark mb-3">
            Últimas fotos subidas
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {uploaded.map((p) => (
              <div key={p.id} className="rounded-lg overflow-hidden border border-border" data-testid={`uploaded-piece-${p.id}`}>
                <SafeImage src={p.file_url} alt={p.prompt} className="w-full aspect-square object-cover" />
                <div className="p-2 text-[10px] text-stone-500 truncate" title={p.file_name}>
                  {p.file_name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generate tab
// ---------------------------------------------------------------------------
function GenerateTab() {
  const [prompt, setPrompt] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState(["instagram_post"]);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState([]); // piezas generadas del último set
  const [credits, setCredits] = useState(null);
  const [plan, setPlan] = useState({ credits_needed: 0, aspects: [] });
  // Fase 22 · identidades de marca
  const [identities, setIdentities] = useState([]);
  const [identityId, setIdentityId] = useState("");
  // Fase 25 · referencias visuales + nota de voz
  const [refs, setRefs] = useState([]); // array de URLs
  const [uploadingRef, setUploadingRef] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [recorder, setRecorder] = useState(null);

  const uploadRefs = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingRef(true);
    try {
      const uploaded = [];
      for (const f of Array.from(files)) {
        const fd = new FormData(); fd.append("file", f);
        try {
          const r = await api.post("/upload/photo?module=creative_ref", fd, { headers: { "Content-Type": "multipart/form-data" } });
          uploaded.push(r.data.url);
        } catch (_) { /* skip */ }
      }
      setRefs((prev) => [...prev, ...uploaded].slice(0, 6));
      if (uploaded.length) toast.success(`${uploaded.length} referencia(s) subida(s)`);
    } finally { setUploadingRef(false); }
  };
  const removeRef = (idx) => setRefs((prev) => prev.filter((_, i) => i !== idx));

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => chunks.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "note.webm");
          const r = await api.post("/creative/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          const text = (r.data?.text || "").trim();
          if (text) {
            setPrompt((prev) => (prev ? prev + " " + text : text));
            toast.success("Nota de voz transcrita");
          } else {
            toast.message("No se detectó texto");
          }
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al transcribir");
        } finally { setTranscribing(false); }
      };
      mr.start();
      setRecorder(mr);
      setRecording(true);
    } catch (e) {
      toast.error("No se pudo acceder al micrófono");
    }
  };
  const stopRecording = () => {
    if (recorder && recorder.state !== "inactive") recorder.stop();
    setRecording(false);
  };

  const loadCredits = async () => {
    try {
      const { data } = await api.get("/credits/usage");
      setCredits(data);
    } catch (_) { /* silencioso */ }
  };
  const loadIdentities = async () => {
    try {
      const { data } = await api.get("/brand-identities");
      const items = data.items || [];
      setIdentities(items);
      const def = items.find((i) => i.default);
      if (def && !identityId) setIdentityId(def.id);
    } catch (_) { /* silencioso */ }
  };
  useEffect(() => { loadCredits(); loadIdentities(); /* eslint-disable-next-line */ }, []);

  // Recalcular plan (créditos necesarios) al cambiar selección
  useEffect(() => {
    const seen = {};
    selectedPlatforms.forEach((p) => {
      const t = PLATFORM_TARGETS.find((x) => x.key === p);
      if (t) seen[t.aspect] = seen[t.aspect] ? [...seen[t.aspect], p] : [p];
    });
    const aspects = Object.entries(seen).map(([aspect, platforms]) => ({ aspect, platforms }));
    setPlan({ credits_needed: aspects.length, aspects });
  }, [selectedPlatforms]);

  const togglePlatform = (key) =>
    setSelectedPlatforms((prev) => prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]);

  const submit = async (e) => {
    e.preventDefault();
    if (prompt.trim().length < 3) return toast.error("Escriba un prompt de al menos 3 caracteres");
    if (selectedPlatforms.length === 0) return toast.error("Seleccione al menos una plataforma");

    // Pre-check contra créditos disponibles
    try {
      const { data: planCheck } = await api.post("/creative/generate-plan", {
        prompt: prompt.trim(),
        platforms: selectedPlatforms,
      });
      if (planCheck.blocked) {
        toast.error(planCheck.block_message);
        await loadCredits();
        return;
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error de plan");
      return;
    }

    setGenerating(true);
    setResults([]);
    const setId = crypto.randomUUID();
    setProgress({ current: 0, total: plan.credits_needed });

    const generated = [];
    for (let i = 0; i < plan.aspects.length; i++) {
      const { aspect, platforms } = plan.aspects[i];
      setProgress({ current: i + 1, total: plan.aspects.length });
      try {
        // Fase 25 · si hay referencias, usar endpoint image-to-image
        const endpoint = refs.length > 0 ? "/creative/generate-from-references" : "/creative/generate";
        const body = refs.length > 0
          ? {
              prompt: prompt.trim(),
              aspect_ratio: aspect,
              reference_urls: refs,
              identity_id: identityId || null,
            }
          : {
              prompt: prompt.trim(),
              aspect_ratio: aspect,
              platforms,
              set_id: setId,
              identity_id: identityId || null,
            };
        const { data } = await api.post(endpoint, body);
        generated.push(data);
        setResults([...generated]);
      } catch (err) {
        const detail = formatApiErrorDetail(err.response?.data?.detail);
        toast.error(detail || `Falló la generación ${i + 1}/${plan.aspects.length}`);
        break;
      }
    }
    setGenerating(false);
    setProgress({ current: 0, total: 0 });
    if (generated.length > 0) {
      toast.success(`Set generado: ${generated.length} variante(s) para ${selectedPlatforms.length} plataforma(s)`);
    }
    await loadCredits();
  };

  const blocked = credits && (
    (credits.company_limit && credits.company_usage >= credits.company_limit.value) ||
    (credits.user_limit && credits.user_usage >= credits.user_limit.value)
  );
  const wouldExceed = credits && !blocked && (
    (credits.company_limit && (credits.company_usage + plan.credits_needed) > credits.company_limit.value) ||
    (credits.user_limit && (credits.user_usage + plan.credits_needed) > credits.user_limit.value)
  );
  const nearLimit = !blocked && credits && (
    (credits.company_limit && (credits.company_usage / credits.company_limit.value) >= 0.8) ||
    (credits.user_limit && (credits.user_usage / credits.user_limit.value) >= 0.8)
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 min-w-0 max-w-full">
      <form onSubmit={submit} className="card-elevated p-6 space-y-5 min-w-0 max-w-full overflow-hidden" data-testid="generate-form">
        {/* Fase 7: Medidor de créditos + advertencias */}
        {credits && (
          <div
            className={`rounded-md border p-4 ${blocked ? "border-destructive/40 bg-destructive/5" : nearLimit ? "border-brand-gold bg-brand-gold-soft/40" : "border-brand-emerald/20 bg-brand-emerald-soft/30"}`}
            data-testid="credit-meter"
          >
            <div className="flex items-center gap-2 mb-3">
              <Zap className={`h-4 w-4 ${blocked ? "text-destructive" : nearLimit ? "text-brand-gold-dark" : "text-brand-emerald-dark"}`} />
              <span className="text-xs uppercase tracking-widest font-semibold text-stone-600">
                Créditos IA · {credits.company_name}
              </span>
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-[10px] text-stone-500 uppercase tracking-widest mb-1">
                  Empresa
                </div>
                <CreditsBar
                  usage={credits.company_usage}
                  limit={credits.company_limit}
                  percent={credits.company_limit ? Math.round((credits.company_usage / credits.company_limit.value) * 100) : null}
                />
              </div>
              {credits.user_limit && (
                <div>
                  <div className="text-[10px] text-stone-500 uppercase tracking-widest mb-1">
                    Tu asignación personal
                  </div>
                  <CreditsBar
                    usage={credits.user_usage}
                    limit={credits.user_limit}
                    percent={Math.round((credits.user_usage / credits.user_limit.value) * 100)}
                  />
                </div>
              )}
            </div>
            {blocked && (
              <div className="mt-3 flex items-start gap-2 rounded bg-white p-3 border border-destructive/30" data-testid="credit-blocked-banner">
                <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                <div className="text-xs text-brand-ink">
                  <div className="flex items-center gap-2">
                    <img src="/assets/logo.png" alt="FStudy" className="h-4 w-4" />
                    <span className="font-serif-display text-sm font-semibold text-destructive">Créditos IA agotados</span>
                  </div>
                  <p className="mt-1 leading-relaxed">
                    Ha alcanzado el límite del periodo. La generación está bloqueada hasta que el Master amplíe el límite o inicie el próximo periodo.
                  </p>
                </div>
              </div>
            )}
            {!blocked && nearLimit && (
              <div className="mt-3 flex items-start gap-2 rounded bg-white p-3 border border-brand-gold/40" data-testid="credit-warning-banner">
                <AlertTriangle className="h-4 w-4 text-brand-gold-dark mt-0.5 shrink-0" />
                <div className="text-xs text-brand-ink">
                  <div className="flex items-center gap-2">
                    <img src="/assets/logo.png" alt="FStudy" className="h-4 w-4" />
                    <span className="font-serif-display text-sm font-semibold text-brand-gold-dark">
                      {credits.company_name} está por agotar sus créditos
                    </span>
                  </div>
                  <p className="mt-1 leading-relaxed">
                    Le queda menos del 20% de la asignación de este {credits.company_limit?.period === "week" ? "semana" : "mes"}. Considere ampliar el límite antes de que se agote.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
            Identidad de marca (opcional)
          </Label>
          <div className="flex flex-wrap gap-2" data-testid="identity-selector">
            <button
              type="button"
              onClick={() => setIdentityId("")}
              className={[
                "px-3 py-1.5 text-sm rounded-full border transition-all",
                !identityId
                  ? "bg-brand-emerald text-white border-brand-emerald shadow-sm"
                  : "bg-white text-stone-600 border-stone-200 hover:border-brand-emerald/40",
              ].join(" ")}
              data-testid="identity-none"
            >
              Sin identidad
            </button>
            {identities.map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => setIdentityId(it.id)}
                className={[
                  "px-3 py-1.5 text-sm rounded-full border transition-all",
                  identityId === it.id
                    ? "bg-brand-gold text-brand-ink border-brand-gold shadow-sm font-semibold"
                    : "bg-white text-stone-700 border-stone-200 hover:border-brand-gold/40",
                ].join(" ")}
                data-testid={`identity-${it.id}`}
                title={it.description || it.name}
              >
                {it.name}
                {it.default && <span className="ml-1 text-[10px] text-brand-emerald-dark">•</span>}
              </button>
            ))}
            <Link
              to="/motor-creativo/identidades"
              className="px-3 py-1.5 text-xs text-brand-emerald-dark underline"
              data-testid="identity-manage"
            >
              Gestionar identidades →
            </Link>
          </div>
          {identityId && (
            <p className="text-[11px] text-stone-500">
              La identidad se aplicará al prompt final automáticamente (estilo, tono, colores).
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
              Prompt creativo
            </Label>
            <div className="flex gap-1">
              {/* Fase 25 · Nota de voz → transcripción */}
              {!recording ? (
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={startRecording}
                  disabled={generating || transcribing}
                  data-testid="btn-voice-note"
                  className="h-8 text-brand-emerald-dark border-brand-emerald hover:bg-brand-emerald-soft"
                >
                  {transcribing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Mic className="h-3 w-3 mr-1" />}
                  {transcribing ? "Transcribiendo…" : "Nota de voz"}
                </Button>
              ) : (
                <Button
                  type="button" size="sm"
                  onClick={stopRecording}
                  data-testid="btn-stop-recording"
                  className="h-8 bg-destructive text-white hover:bg-destructive/90 animate-pulse"
                >
                  <Mic className="h-3 w-3 mr-1" /> Detener
                </Button>
              )}
              {/* Fase 25 · Referencias visuales */}
              <label className="inline-flex items-center gap-1 rounded-md border border-input bg-white px-2 h-8 text-xs cursor-pointer hover:bg-stone-50 text-brand-emerald-dark">
                {uploadingRef ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageIcon className="h-3 w-3" />}
                <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => uploadRefs(e.target.files)} data-testid="btn-add-references" />
                Referencias
              </label>
            </div>
          </div>
          <Textarea
            rows={5}
            placeholder="Ej: cartel elegante minimalista para lanzar una colección otoñal de FStudy, tipografía serif dorada, papel texturizado esmeralda…"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            data-testid="generate-prompt"
            disabled={generating || blocked}
          />
          {refs.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2" data-testid="creative-refs-preview">
              {refs.map((u, i) => (
                <div key={i} className="relative w-16 h-16 rounded border overflow-hidden">
                  <img src={u} alt={`ref-${i}`} className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeRef(i)} className="absolute top-0.5 right-0.5 bg-brand-ink/70 text-white rounded-full p-0.5" data-testid={`creative-ref-remove-${i}`}>
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              ))}
              <p className="w-full text-[10px] text-brand-emerald-dark">La IA usará estas imágenes como referencia visual (image-to-image).</p>
            </div>
          )}
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
            Plataformas destino
          </Label>
          <p className="text-[10px] text-stone-500 mb-2">
            Seleccione una o varias. Aspectos idénticos comparten UNA imagen (ahorra créditos).
          </p>
          <div className="grid grid-cols-2 gap-2" data-testid="platform-selector">
            {PLATFORM_TARGETS.map((t) => {
              const Icon = t.icon;
              const selected = selectedPlatforms.includes(t.key);
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => togglePlatform(t.key)}
                  disabled={generating || blocked}
                  className={`flex items-center gap-2 p-2 rounded-md border transition text-left ${selected ? "border-brand-gold bg-brand-gold-soft shadow-gold" : "border-border bg-white hover:bg-stone-50"}`}
                  data-testid={`platform-target-${t.key}`}
                >
                  <Icon className="h-4 w-4 shrink-0 text-brand-emerald-dark" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-brand-ink leading-tight">{t.label}</div>
                    <div className="text-[10px] text-stone-500 font-mono">{t.dim}</div>
                  </div>
                  {selected && <Check className="h-3.5 w-3.5 text-brand-gold-dark" />}
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-md bg-stone-50 border border-border p-3 flex items-center justify-between gap-2 flex-wrap" data-testid="generate-plan-summary">
          <div className="text-xs text-stone-600 min-w-0 flex-1">
            {plan.credits_needed === 0 ? (
              <>Seleccione al menos una plataforma.</>
            ) : (
              <>
                Esta generación usará <strong className="text-brand-emerald-dark">{plan.credits_needed} crédito(s)</strong>
                {plan.aspects.length < selectedPlatforms.length && (
                  <span className="text-stone-500"> · deduplicado por aspecto</span>
                )}
              </>
            )}
          </div>
          <div className="flex gap-1 flex-wrap justify-end min-w-0">
            {plan.aspects.map((a) => (
              <span key={a.aspect} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-brand-gold-soft text-brand-gold-dark border border-brand-gold/30 whitespace-nowrap">
                {a.aspect} × {a.platforms.length}
              </span>
            ))}
          </div>
        </div>

        <Button
          type="submit"
          disabled={generating || blocked || wouldExceed || plan.credits_needed === 0}
          data-testid="generate-submit"
          className="w-full h-12 bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold font-semibold disabled:opacity-60"
        >
          {generating ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generando formato {progress.current} de {progress.total}…</>
          ) : blocked ? (
            <>Créditos agotados</>
          ) : wouldExceed ? (
            <>El lote excede créditos disponibles</>
          ) : (
            <><Sparkles className="h-4 w-4 mr-2" /> Generar · {plan.credits_needed} crédito(s)</>
          )}
        </Button>
        <p className="text-xs text-stone-500">
          Cada generación real toma 5-15 segundos. Se ejecutan en secuencia para poder ver el progreso.
        </p>
      </form>

      <div className="card-elevated p-6" data-testid="generate-preview">
        <div className="text-xs uppercase tracking-widest font-semibold text-stone-600 mb-3">
          Vista previa {results.length > 0 && `· ${results.length} variante(s)`}
        </div>
        {generating ? (
          <div className="aspect-square rounded-lg border border-dashed border-brand-emerald bg-brand-emerald-soft/40 flex flex-col items-center justify-center gap-3 animate-pulse" data-testid="generate-progress">
            <Loader2 className="h-8 w-8 animate-spin text-brand-emerald" />
            <p className="text-sm text-brand-emerald-dark font-semibold">
              Componiendo formato {progress.current} de {progress.total}…
            </p>
            <div className="w-3/4 h-2 rounded-full bg-white overflow-hidden">
              <div className="h-full bg-brand-emerald transition-all"
                   style={{ width: `${(progress.current / Math.max(1, progress.total)) * 100}%` }} />
            </div>
          </div>
        ) : results.length > 0 ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="generate-result-grid">
              {results.map((r) => (
                <div key={r.id} className="rounded-lg border border-border overflow-hidden">
                  <SafeImage src={r.file_url} alt={r.prompt}
                       className="w-full aspect-square object-cover"
                       testid={`generate-result-${r.id}`} />
                  <div className="p-2 text-[10px] text-stone-500 flex items-center justify-between gap-2">
                    <span className="font-mono">{r.aspect_ratio}</span>
                    <PlatformBadges platforms={r.platforms} testid={`preview-platforms-${r.id}`} />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-stone-500">
              {results[0]?.status === "aprobada"
                ? "Auto-aprobado (Master). Ya disponible en la galería."
                : "Piezas esperando aprobación del Master."}
            </p>
          </div>
        ) : (
          <div className="aspect-square rounded-lg border border-dashed border-stone-300 flex items-center justify-center">
            <div className="text-center text-stone-400 text-sm">
              <ImageIcon className="h-8 w-8 mx-auto mb-2" />
              Sus imágenes aparecerán aquí (una por cada aspecto único).
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gallery
// ---------------------------------------------------------------------------
function GalleryTab({ isMaster, currentUser, activeTab }) {
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [authorFilter, setAuthorFilter] = useState("all");
  // Fase 25 · Editar con IA
  const [editWithAI, setEditWithAI] = useState(null); // piece being edited
  const [editPrompt, setEditPrompt] = useState("");
  const [editing, setEditing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (authorFilter === "mine") params.only_mine = true;
      const { data } = await api.get("/creative/pieces", { params });
      setPieces(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [statusFilter, authorFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  // Fase 27 · Refetch al entrar a la tab (soluciona "no aparecen piezas recién creadas")
  useEffect(() => {
    if (activeTab === "galeria") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const remove = async (p) => {
    if (!window.confirm("¿Eliminar esta pieza?")) return;
    try {
      await api.delete(`/creative/pieces/${p.id}`);
      toast.success("Pieza eliminada");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  // Fase 25 · Editar pieza con IA
  const doEditWithAI = async () => {
    if (!editWithAI || !editPrompt.trim()) { toast.error("Prompt de edición requerido"); return; }
    setEditing(true);
    try {
      await api.post(`/creative/pieces/${editWithAI.id}/edit-with-ai`, { prompt: editPrompt });
      toast.success("Nueva versión generada");
      setEditWithAI(null);
      setEditPrompt("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al editar");
    } finally { setEditing(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-stone-500">
          <Filter className="h-3.5 w-3.5" /> Filtros
        </div>
        <div className="w-48">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="gallery-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isMaster && (
          <div className="w-56">
            <Select value={authorFilter} onValueChange={setAuthorFilter}>
              <SelectTrigger data-testid="gallery-author-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todo el equipo</SelectItem>
                <SelectItem value="mine">Solo mis piezas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="card-elevated p-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>
      ) : pieces.length === 0 ? (
        <div className="card-elevated p-16 text-center text-stone-500">Sin piezas todavía.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {pieces.map((p) => (
            <div key={p.id} className="card-elevated overflow-hidden" data-testid={`piece-card-${p.id}`}>
              <SafeImage
                src={p.file_url}
                alt={p.prompt}
                className="w-full aspect-square object-cover"
                loading="lazy"
                testid={`piece-image-${p.id}`}
              />
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Badge className={`border font-semibold ${STATUS_STYLES[p.status]}`}>
                    {STATUS_LABELS[p.status]}
                  </Badge>
                  <span className="text-xs text-stone-400 font-mono">{p.aspect_ratio}</span>
                </div>
                <PlatformBadges platforms={p.platforms} testid={`piece-platforms-${p.id}`} />
                <p className="text-sm text-stone-700 line-clamp-2">{p.prompt}</p>
                <div className="flex items-center justify-between text-xs text-stone-500">
                  <span className="flex items-center gap-1"><User className="h-3 w-3" /> {p.author_name}</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmt(p.created_at)}</span>
                </div>
                {p.reject_reason && (
                  <div className="text-xs bg-destructive/10 text-destructive p-2 rounded">
                    Motivo del rechazo: {p.reject_reason}
                  </div>
                )}
                {(isMaster || p.author_id === currentUser?.id) && (
                  <div className="pt-1 flex flex-wrap gap-1">
                    <Button size="sm" variant="ghost" asChild className="h-8" data-testid={`piece-download-${p.id}`}>
                      <a href={assetUrl(p.file_url)} download target="_blank" rel="noreferrer">
                        <Download className="h-3.5 w-3.5 mr-1" /> Descargar
                      </a>
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditWithAI(p)} className="h-8 text-brand-emerald-dark hover:bg-brand-emerald-soft" data-testid={`piece-edit-ai-${p.id}`}>
                      <Wand2 className="h-3.5 w-3.5 mr-1" /> Editar con IA
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(p)} className="text-destructive hover:bg-destructive/10 h-8" data-testid={`piece-delete-${p.id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Eliminar
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Fase 25 · Diálogo editar con IA */}
      <Dialog open={!!editWithAI} onOpenChange={(v) => { if (!v) { setEditWithAI(null); setEditPrompt(""); } }}>
        <DialogContent className="max-w-lg" data-testid="edit-with-ai-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Editar pieza con IA</DialogTitle>
            <DialogDescription>La IA generará una nueva versión encadenada. Consume 1 crédito.</DialogDescription>
          </DialogHeader>
          {editWithAI && (
            <div className="space-y-3">
              <SafeImage src={editWithAI.file_url} alt="ref" className="w-full aspect-square object-cover rounded border max-h-48" testid={`edit-ai-ref-${editWithAI.id}`} />
              <div className="space-y-1.5">
                <Label>Instrucción de edición</Label>
                <Textarea rows={3} value={editPrompt} onChange={(e) => setEditPrompt(e.target.value)} placeholder="Ej: cambia el fondo a blanco, agrega texto 'OFERTA' en la esquina…" data-testid="edit-ai-prompt" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditWithAI(null); setEditPrompt(""); }}>Cancelar</Button>
            <Button onClick={doEditWithAI} disabled={editing} data-testid="btn-do-edit-ai" className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">
              {editing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Wand2 className="h-4 w-4 mr-2" /> Editar con IA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pending (Master)
// ---------------------------------------------------------------------------
function PendingTab({ isMaster }) {
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectOpen, setRejectOpen] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/creative/pending");
      setPieces(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (p) => {
    try {
      await api.post(`/creative/pieces/${p.id}/approve`);
      toast.success("Pieza aprobada");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const doReject = async () => {
    if (!rejectOpen) return;
    try {
      await api.post(`/creative/pieces/${rejectOpen.id}/reject`, { reason });
      toast.success("Pieza rechazada");
      setRejectOpen(null);
      setReason("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  if (!isMaster) {
    return (
      <div className="card-elevated p-8 text-center text-stone-500">
        Solo el rol Master puede aprobar o rechazar piezas.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="card-elevated p-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>
      ) : pieces.length === 0 ? (
        <div className="card-elevated p-16 text-center text-stone-500">
          ✨ Sin piezas pendientes. Todo revisado.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {pieces.map((p) => (
            <div key={p.id} className="card-elevated overflow-hidden flex flex-col md:flex-row" data-testid={`pending-card-${p.id}`}>
              <SafeImage src={p.file_url} alt="" className="w-full md:w-48 aspect-square object-cover shrink-0" testid={`pending-image-${p.id}`} />
              <div className="p-5 flex-1 flex flex-col gap-3">
                <div className="text-xs text-stone-500 flex items-center gap-3">
                  <span><User className="inline h-3 w-3" /> {p.author_name}</span>
                  <span>{p.aspect_ratio}</span>
                  <span>{fmt(p.created_at)}</span>
                </div>
                <PlatformBadges platforms={p.platforms} testid={`pending-platforms-${p.id}`} />
                <p className="text-sm text-brand-ink font-medium line-clamp-4 flex-1">{p.prompt}</p>
                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    onClick={() => approve(p)}
                    data-testid={`approve-${p.id}`}
                    className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold flex-1"
                  >
                    <Check className="h-4 w-4 mr-1.5" /> Aprobar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setRejectOpen(p); setReason(""); }}
                    data-testid={`reject-${p.id}`}
                    className="border-destructive text-destructive hover:bg-destructive/10"
                  >
                    <X className="h-4 w-4 mr-1.5" /> Rechazar
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!rejectOpen} onOpenChange={(v) => !v && setRejectOpen(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Rechazar pieza</DialogTitle>
            <DialogDescription>Puede añadir un motivo para que el autor lo vea.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
              Motivo (opcional)
            </Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(null)}>Cancelar</Button>
            <Button onClick={doReject} data-testid="reject-submit" className="bg-destructive text-white hover:bg-destructive/90">
              Rechazar pieza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Video placeholder (elegant, disabled)
// ---------------------------------------------------------------------------
function VideoTab() {
  return (
    <div className="card-elevated p-10" data-testid="video-placeholder">
      <div className="flex flex-col md:flex-row gap-6 items-center">
        <div className="h-24 w-24 rounded-2xl bg-gradient-to-br from-brand-emerald to-brand-emerald-dark flex items-center justify-center shrink-0">
          <Video className="h-11 w-11 text-brand-gold" strokeWidth={1.5} />
        </div>
        <div className="flex-1 text-center md:text-left">
          <div className="flex items-center gap-2 justify-center md:justify-start">
            <h3 className="font-serif-display text-2xl text-brand-ink font-semibold">
              Generación de Video
            </h3>
            <Badge className="bg-brand-gold-soft text-brand-gold-dark border border-brand-gold/40 font-semibold">
              Próximamente
            </Badge>
          </div>
          <p className="text-sm text-stone-500 mt-2">
            Genere reels y clips cortos a partir de un prompt o combinando imágenes de la
            galería. Se activará cuando el proveedor de video esté disponible en la
            plataforma.
          </p>
        </div>
        <Button disabled className="opacity-50 cursor-not-allowed">
          <Video className="h-4 w-4 mr-2" /> Generar video
        </Button>
      </div>
    </div>
  );
}
